'use client';

import useSWR, { KeyedMutator } from 'swr';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useLiveAccount } from '@/lib/live-trading/useLiveTradingData';
import {
  useTrackingWalletsApi,
  type TrackingWalletsResponse,
} from '@/components/predic/hooks/use-tracking-wallets';

export interface TrackingWallets {
  /** 托管系统钱包（account.deposit_wallet_address），未开通时为 null */
  systemWallet: string | null;
  /** 用户手动追踪的钱包地址列表 */
  trackWallets: string[];
}

interface TrackingWalletsContextType {
  wallets: TrackingWallets;
  /** systemWallet + trackWallets 合并去重，供资产/持仓类查询使用 */
  allWallets: string[];
  addWallets: (wallet: string[]) => void;
  removeWallets: (wallet: string[]) => void;
  clearWallets: () => void;
  mutate: KeyedMutator<TrackingWalletsResponse>;
  systemAccountMutate: KeyedMutator<any>;
  loading: boolean;
  error?: any;
}
const TrackingWalletsContext = createContext<TrackingWalletsContextType | null>(null);

export function useTrackingWallets() {
  return useContext(TrackingWalletsContext) || ({} as TrackingWalletsContextType);
}

export default function TrackingWalletsProvider({ children }: { children: ReactNode }) {
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const [trackWallets, setTrackWallets] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const { account, isLoading: accountLoading, mutate: systemAccountMutate } = useLiveAccount();
  const systemWallet = account?.deposit_wallet_address ?? null;

  const { error, mutate } = useTrackingWalletsApi({
    enabled: !!apiAccessToken,
    swr: {
      onSuccess: (res) => {
        setTrackWallets(res.data.map((wallet) => wallet.address));
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    },
  });

  const removeWallets = useCallback(
    (removedWallets: string[]) => {
      const removedWalletSet = new Set(removedWallets);
      setTrackWallets((prevWallets) =>
        prevWallets.filter((wallet) => !removedWalletSet.has(wallet)),
      );
      mutate();
    },
    [mutate],
  );

  const addWallets = useCallback(
    (addedWallets: string[]) => {
      setTrackWallets((prevWallets) => Array.from(new Set([...prevWallets, ...addedWallets])));
      mutate();
    },
    [mutate],
  );

  const clearWallets = useCallback(() => {
    setTrackWallets([]);
  }, []);

  const wallets = useMemo<TrackingWallets>(
    () => ({ systemWallet, trackWallets }),
    [systemWallet, trackWallets],
  );

  const allWallets = useMemo(
    () => Array.from(new Set([...(systemWallet ? [systemWallet] : []), ...trackWallets])),
    [systemWallet, trackWallets],
  );

  const value = useMemo(
    () => ({
      wallets,
      allWallets,
      addWallets,
      removeWallets,
      clearWallets,
      mutate,
      systemAccountMutate,
      loading: loading || accountLoading,
      error,
    }),
    [
      addWallets,
      allWallets,
      accountLoading,
      clearWallets,
      error,
      loading,
      mutate,
      removeWallets,
      wallets,
      systemAccountMutate,
    ],
  );

  return (
    <TrackingWalletsContext.Provider value={value}>{children}</TrackingWalletsContext.Provider>
  );
}
